import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from 'contexts/AuthContext';
import { supabase } from 'lib/supabase';
import ProfessionalDashboardNavbar from 'pages/professional-dashboard/components/ProfessionalDashboardNavbar';
import Footer from 'pages/landing-page/components/Footer';
import Icon from 'components/AppIcon';
import { ChevronLeft, ChevronRight, ArrowLeft } from 'lucide-react';

// ── helpers ──────────────────────────────────────────────────────────────────

const relativeDate = (dateStr) => {
  if (!dateStr) return '';
  const diff = Math.floor((Date.now() - new Date(dateStr)?.getTime()) / 1000);
  if (diff < 60) return 'poco fa';
  if (diff < 3600) return `${Math.floor(diff / 60)} minuti fa`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} ore fa`;
  if (diff < 604800) return `${Math.floor(diff / 86400)} giorni fa`;
  if (diff < 2592000) return `${Math.floor(diff / 604800)} settimane fa`;
  if (diff < 31536000) return `${Math.floor(diff / 2592000)} mesi fa`;
  return `${Math.floor(diff / 31536000)} anni fa`;
};

const avg = (arr) => {
  if (!arr?.length) return 0;
  return arr?.reduce((s, v) => s + (v || 0), 0) / arr?.length;
};

const STORAGE_BASE =
  'https://vltfzozjztfaeifjqpcg.supabase.co/storage/v1/object/public/review-photos/';

// ── sub-components ────────────────────────────────────────────────────────────

const StarRow = ({ value, size = 18, color = '#fbbf24', emptyColor = '#d1d5db' }) => {
  const stars = [];
  for (let i = 1; i <= 5; i++) {
    const filled = value >= i;
    const half = !filled && value >= i - 0.5;
    stars?.push(
      <span key={i} style={{ position: 'relative', display: 'inline-block', width: size, height: size }}>
        {/* empty star */}
        <svg width={size} height={size} viewBox="0 0 24 24" fill={emptyColor}>
          <polygon points="12,2 15.09,8.26 22,9.27 17,14.14 18.18,21.02 12,17.77 5.82,21.02 7,14.14 2,9.27 8.91,8.26" />
        </svg>
        {/* filled overlay */}
        {(filled || half) && (
          <svg
            width={size}
            height={size}
            viewBox="0 0 24 24"
            fill={color}
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              clipPath: half ? 'inset(0 50% 0 0)' : 'none',
            }}
          >
            <polygon points="12,2 15.09,8.26 22,9.27 17,14.14 18.18,21.02 12,17.77 5.82,21.02 7,14.14 2,9.27 8.91,8.26" />
          </svg>
        )}
      </span>
    );
  }
  return <span style={{ display: 'inline-flex', gap: 2 }}>{stars}</span>;
};

const DistributionBar = ({ label, count, total }) => {
  const pct = total > 0 ? Math.round((count / total) * 100) : 0;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
      <span style={{ fontSize: 13, color: '#6b7280', width: 28, textAlign: 'right', flexShrink: 0 }}>
        {label} ★
      </span>
      <div style={{ flex: 1, height: 8, borderRadius: 4, background: '#f3f4f6', overflow: 'hidden' }}>
        <div style={{ width: `${pct}%`, height: '100%', background: '#2780b5', borderRadius: 4, transition: 'width 0.4s' }} />
      </div>
      <span style={{ fontSize: 12, color: '#9ca3af', width: 32, flexShrink: 0 }}>{pct}%</span>
    </div>
  );
};

const Toast = ({ message, onClose }) => (
  <div
    style={{
      position: 'fixed',
      bottom: 24,
      right: 24,
      background: '#013776',
      color: '#fff',
      padding: '12px 20px',
      borderRadius: 10,
      fontWeight: 600,
      fontSize: 14,
      zIndex: 9999,
      boxShadow: '0 4px 20px rgba(0,0,0,0.25)',
      display: 'flex',
      alignItems: 'center',
      gap: 10,
    }}
  >
    <Icon name="CheckCircle" size={18} color="#4ade80" />
    {message}
    <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#fff', cursor: 'pointer', marginLeft: 4 }}>
      <Icon name="X" size={14} />
    </button>
  </div>
);

const Lightbox = ({ lightbox, onClose, onPrev, onNext }) => {
  const { photos, currentIndex } = lightbox;
  const currentPhoto = photos?.[currentIndex];
  const url = currentPhoto
    ? `${STORAGE_BASE}${currentPhoto?.storage_path || currentPhoto}`
    : '';
  const total = photos?.length || 0;

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9998,
        flexDirection: 'column',
      }}
    >
      <div style={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
        onClick={(e) => e?.stopPropagation()}
      >
        {/* Prev arrow */}
        {currentIndex > 0 && (
          <button
            onClick={(e) => { e?.stopPropagation(); onPrev(); }}
            style={{
              position: 'absolute', left: 16, top: '50%', transform: 'translateY(-50%)',
              background: 'rgba(255,255,255,0.2)', backdropFilter: 'blur(4px)',
              border: 'none', borderRadius: '50%', width: 40, height: 40,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              cursor: 'pointer', zIndex: 1,
            }}
            onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.4)'}
            onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.2)'}
          >
            <ChevronLeft size={24} color="white" />
          </button>
        )}

        <img
          src={url}
          alt="Foto recensione"
          style={{ maxWidth: '90vw', maxHeight: '80vh', borderRadius: 8, objectFit: 'contain' }}
        />

        {/* Next arrow */}
        {currentIndex < total - 1 && (
          <button
            onClick={(e) => { e?.stopPropagation(); onNext(); }}
            style={{
              position: 'absolute', right: 16, top: '50%', transform: 'translateY(-50%)',
              background: 'rgba(255,255,255,0.2)', backdropFilter: 'blur(4px)',
              border: 'none', borderRadius: '50%', width: 40, height: 40,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              cursor: 'pointer', zIndex: 1,
            }}
            onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.4)'}
            onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.2)'}
          >
            <ChevronRight size={24} color="white" />
          </button>
        )}
      </div>

      {/* Counter */}
      {total > 1 && (
        <p style={{ color: 'white', fontSize: 14, textAlign: 'center', marginTop: 12 }}
          onClick={(e) => e?.stopPropagation()}
        >
          {currentIndex + 1} / {total}
        </p>
      )}

      {/* Close button */}
      <button
        onClick={onClose}
        style={{
          position: 'absolute', top: 20, right: 20,
          background: 'rgba(255,255,255,0.15)', border: 'none', borderRadius: '50%',
          width: 40, height: 40, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}
      >
        <Icon name="X" size={20} color="#fff" />
      </button>
    </div>
  );
};

// ── ResponseSection ───────────────────────────────────────────────────────────

const ResponseSection = ({ review, onSaved }) => {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(review?.professional_response || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const handleSave = async () => {
    if (!text?.trim()) return;
    setSaving(true);
    setError(null);
    const { error: saveError } = await supabase?.from('reviews')?.update({
        professional_response: text?.trim(),
        professional_response_at: new Date()?.toISOString(),
      })?.eq('id', review?.id);
    setSaving(false);
    if (saveError) {
      setError('Errore nel salvataggio. Riprova.');
    } else {
      setEditing(false);
      onSaved();
      // Notify client about the response (best-effort)
      try {
        supabase?.functions?.invoke('notify-response-published', {
          body: { review_id: review?.id }
        });
      } catch (e) {
        console.error('notify-response-published error:', e);
      }
    }
  };

  const hasResponse = !!review?.professional_response;
  const canEdit = review?.can_still_edit_response;

  // Countdown hours remaining
  const hoursLeft = review?.professional_response_at
    ? Math.max(0, Math.ceil((new Date(review.professional_response_at)?.getTime() + 72 * 3600000 - Date.now()) / 3600000))
    : null;

  if (!hasResponse && canEdit) {
    // No response yet, can respond
    if (!editing) {
      return (
        <button
          onClick={() => setEditing(true)}
          style={{
            marginTop: 12,
            padding: '8px 16px',
            background: '#32b3e5',
            color: '#fff',
            border: 'none',
            borderRadius: 8,
            fontWeight: 600,
            fontSize: 13,
            cursor: 'pointer',
          }}
        >
          Rispondi alla recensione
        </button>
      );
    }
    return (
      <div style={{ marginTop: 12 }}>
        <textarea
          value={text}
          onChange={(e) => setText(e?.target?.value?.slice(0, 500))}
          placeholder="Scrivi la tua risposta..."
          rows={4}
          style={{
            width: '100%', padding: '10px 12px', borderRadius: 8,
            border: '1.5px solid #d1d5db', fontSize: 14, resize: 'vertical',
            fontFamily: 'inherit', outline: 'none',
          }}
        />
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 4 }}>
          <span style={{ fontSize: 12, color: '#9ca3af' }}>{text?.length}/500</span>
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              onClick={() => { setEditing(false); setText(''); }}
              style={{ padding: '7px 14px', borderRadius: 8, border: '1px solid #d1d5db', background: '#fff', fontSize: 13, cursor: 'pointer', fontWeight: 500 }}
            >
              Annulla
            </button>
            <button
              onClick={handleSave}
              disabled={saving || !text?.trim()}
              style={{
                padding: '7px 14px', borderRadius: 8, border: 'none',
                background: saving || !text?.trim() ? '#93c5fd' : '#2780b5',
                color: '#fff', fontSize: 13, fontWeight: 600, cursor: saving ? 'not-allowed' : 'pointer',
              }}
            >
              {saving ? 'Salvataggio...' : 'Salva risposta'}
            </button>
          </div>
        </div>
        {error && <p style={{ color: '#ef4444', fontSize: 12, marginTop: 4 }}>{error}</p>}
      </div>
    );
  }

  if (hasResponse) {
    return (
      <div style={{ marginTop: 12, background: '#f3f4f6', borderRadius: 8, padding: '12px 14px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 6 }}>
          <span style={{ fontSize: 12, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            La tua risposta
          </span>
          {canEdit ? (
            <span style={{ fontSize: 11, color: '#9ca3af' }}>
              Modificabile per altre {hoursLeft}h
            </span>
          ) : (
            <span style={{ fontSize: 11, color: '#9ca3af', background: '#e5e7eb', padding: '2px 8px', borderRadius: 20 }}>
              Pubblicata
            </span>
          )}
        </div>
        {editing ? (
          <>
            <textarea
              value={text}
              onChange={(e) => setText(e?.target?.value?.slice(0, 500))}
              rows={4}
              style={{
                width: '100%', padding: '10px 12px', borderRadius: 8,
                border: '1.5px solid #d1d5db', fontSize: 14, resize: 'vertical',
                fontFamily: 'inherit', outline: 'none',
              }}
            />
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 4 }}>
              <span style={{ fontSize: 12, color: '#9ca3af' }}>{text?.length}/500</span>
              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  onClick={() => { setEditing(false); setText(review?.professional_response || ''); }}
                  style={{ padding: '7px 14px', borderRadius: 8, border: '1px solid #d1d5db', background: '#fff', fontSize: 13, cursor: 'pointer', fontWeight: 500 }}
                >
                  Annulla
                </button>
                <button
                  onClick={handleSave}
                  disabled={saving || !text?.trim()}
                  style={{
                    padding: '7px 14px', borderRadius: 8, border: 'none',
                    background: saving || !text?.trim() ? '#93c5fd' : '#2780b5',
                    color: '#fff', fontSize: 13, fontWeight: 600, cursor: saving ? 'not-allowed' : 'pointer',
                  }}
                >
                  {saving ? 'Salvataggio...' : 'Salva risposta'}
                </button>
              </div>
            </div>
            {error && <p style={{ color: '#ef4444', fontSize: 12, marginTop: 4 }}>{error}</p>}
          </>
        ) : (
          <>
            <p style={{ fontSize: 14, color: '#374151', lineHeight: 1.6, margin: 0 }}>
              {review?.professional_response}
            </p>
            {review?.professional_response_at && (
              <p style={{ fontSize: 12, color: '#9ca3af', marginTop: 4 }}>
                {relativeDate(review?.professional_response_at)}
              </p>
            )}
            {canEdit && (
              <button
                onClick={() => setEditing(true)}
                style={{ marginTop: 8, fontSize: 12, color: '#2780b5', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600, padding: 0 }}
              >
                Modifica risposta
              </button>
            )}
          </>
        )}
      </div>
    );
  }

  return null;
};

// ── ReviewCard ────────────────────────────────────────────────────────────────

const ReviewCard = ({ review, onResponseSaved, onLightbox }) => {
  const photos = Array.isArray(review?.photos) ? review?.photos : [];

  return (
    <div
      style={{
        background: '#fff',
        borderRadius: 16,
        border: '1px solid #e5e7eb',
        padding: '20px 24px',
        marginBottom: 16,
      }}
    >
      {/* Top row */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 8 }}>
        <div>
          <span style={{ fontWeight: 700, fontSize: 15, color: '#111827' }}>
            {review?.client_display_name || 'Cliente'}
          </span>
          {review?.service_request_title && (
            <span style={{ fontSize: 12, color: '#9ca3af', marginLeft: 8 }}>
              su {review?.service_request_title}
            </span>
          )}
        </div>
        <span style={{ fontSize: 12, color: '#9ca3af' }}>{relativeDate(review?.created_at)}</span>
      </div>
      {/* Stars */}
      <div style={{ marginTop: 8 }}>
        <StarRow value={review?.rating} size={18} />
      </div>
      {/* Sub-ratings */}
      {(review?.rating_punctuality || review?.rating_quality || review?.rating_price) && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 16px', marginTop: 8 }}>
          {review?.rating_punctuality && (
            <span style={{ fontSize: 12, color: '#6b7280', display: 'flex', alignItems: 'center', gap: 4 }}>
              Puntualità <StarRow value={review?.rating_punctuality} size={12} />
            </span>
          )}
          {review?.rating_quality && (
            <span style={{ fontSize: 12, color: '#6b7280', display: 'flex', alignItems: 'center', gap: 4 }}>
              Qualità <StarRow value={review?.rating_quality} size={12} />
            </span>
          )}
          {review?.rating_price && (
            <span style={{ fontSize: 12, color: '#6b7280', display: 'flex', alignItems: 'center', gap: 4 }}>
              Prezzo <StarRow value={review?.rating_price} size={12} />
            </span>
          )}
        </div>
      )}
      {/* Text */}
      {review?.text && (
        <p style={{ marginTop: 10, fontSize: 14, color: '#374151', lineHeight: 1.65 }}>{review?.text}</p>
      )}
      {/* Photos */}
      {photos?.length > 0 && (
        <div style={{ display: 'flex', gap: 8, marginTop: 12, overflowX: 'auto', paddingBottom: 4 }}>
          {photos?.map((photo, idx) => {
            const url = `${STORAGE_BASE}${photo?.storage_path || photo}`;
            return (
              <img
                key={idx}
                src={url}
                alt={`Foto ${idx + 1}`}
                onClick={() => onLightbox({ photos, currentIndex: idx })}
                style={{
                  width: 72, height: 72, objectFit: 'cover', borderRadius: 8,
                  cursor: 'pointer', flexShrink: 0, border: '1px solid #e5e7eb',
                }}
              />
            );
          })}
        </div>
      )}
      {/* Response */}
      <ResponseSection review={review} onSaved={onResponseSaved} />
    </div>
  );
};

// ── Main Page ─────────────────────────────────────────────────────────────────

const ProfessionalRecensioni = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();

  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState(null);
  const [lightbox, setLightbox] = useState(null);

  // Keyboard navigation for lightbox
  useEffect(() => {
    if (!lightbox) return;
    const handleKey = (e) => {
      if (e?.key === 'Escape') {
        setLightbox(null);
      } else if (e?.key === 'ArrowLeft') {
        setLightbox(prev => prev && prev?.currentIndex > 0
          ? { ...prev, currentIndex: prev?.currentIndex - 1 }
          : prev
        );
      } else if (e?.key === 'ArrowRight') {
        setLightbox(prev => prev && prev?.currentIndex < prev?.photos?.length - 1
          ? { ...prev, currentIndex: prev?.currentIndex + 1 }
          : prev
        );
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [lightbox]);

  // Access guard
  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate('/login-form', { replace: true });
      return;
    }
    // role check
    supabase?.from('users')?.select('role')?.eq('id', user?.id)?.single()?.then(({ data }) => {
        const role = data?.role;
        if (role && role !== 'professionista' && role !== 'admin' && role !== 'superadmin') {
          navigate('/professional-dashboard', { replace: true });
        }
      });
  }, [user, authLoading, navigate]);

  const fetchReviews = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      const { data, error: rpcError } = await supabase?.rpc('get_my_reviews_as_professional');
      if (rpcError) throw rpcError;
      setReviews(data || []);
    } catch (err) {
      console.error('Error fetching reviews:', err);
      setError('Errore nel caricamento delle recensioni.');
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchReviews();
  }, [fetchReviews]);

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(null), 3500);
  };

  const handleResponseSaved = () => {
    showToast('Risposta pubblicata');
    fetchReviews();
  };

  // Aggregate calculations
  const totalCount = reviews?.length;
  const avgRating = totalCount > 0 ? avg(reviews?.map((r) => r?.rating)) : 0;
  const dist = [5, 4, 3, 2, 1]?.map((star) => ({
    star,
    count: reviews?.filter((r) => Math.round(r?.rating) === star)?.length,
  }));

  if (authLoading || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin" />
          <p className="text-gray-600">Caricamento recensioni...</p>
        </div>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen pt-16"
      style={{
        backgroundImage: "url('/assets/images/professional_dashboard_hero_bright_interior.png')",
        backgroundSize: 'cover',
        backgroundAttachment: 'fixed',
        backgroundColor: 'rgba(255,255,255,0.92)',
        backgroundBlendMode: 'overlay',
      }}
    >
      <ProfessionalDashboardNavbar />
      <div style={{ maxWidth: 900, margin: '0 auto', padding: '40px 16px 60px' }}>
        {/* Back button */}
        <button
          onClick={() => navigate('/professional-dashboard')}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            color: '#6b7280',
            fontSize: 14,
            fontWeight: 500,
            padding: 0,
            marginBottom: 24,
          }}
          onMouseEnter={e => e.currentTarget.style.color = '#2780b5'}
          onMouseLeave={e => e.currentTarget.style.color = '#6b7280'}
        >
          <ArrowLeft size={16} />
          Torna alla dashboard
        </button>

        {/* Page header */}
        <div style={{ marginBottom: 28 }}>
          <h1 style={{ fontSize: 28, fontWeight: 800, color: '#104d98', margin: 0 }}>
            Le mie recensioni
          </h1>
          <p style={{ fontSize: 15, color: '#6b7280', marginTop: 6 }}>
            Leggi e rispondi alle recensioni dei tuoi clienti
          </p>
        </div>

        {error && (
          <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 10, padding: '12px 16px', marginBottom: 20, color: '#b91c1c', fontSize: 14 }}>
            {error}
          </div>
        )}

        {/* Empty state */}
        {totalCount === 0 && !error && (
          <div
            style={{
              background: '#fff', borderRadius: 16, border: '1px solid #e5e7eb',
              padding: '56px 32px', textAlign: 'center',
            }}
          >
            <div style={{ width: 64, height: 64, borderRadius: '50%', background: '#eff6ff', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
              <Icon name="Star" size={28} color="#2780b5" />
            </div>
            <p style={{ fontSize: 18, fontWeight: 700, color: '#374151', marginBottom: 10 }}>
              Le recensioni costruiscono la tua reputazione
            </p>
            <p style={{ fontSize: 14, color: '#9ca3af', maxWidth: 440, margin: '0 auto', lineHeight: 1.65 }}>
              Quando un cliente conferma il lavoro svolto, gli inviamo automaticamente un invito a recensirti. La prima recensione è la più importante: chiedila al tuo prossimo cliente.
            </p>
          </div>
        )}

        {totalCount > 0 && (
          <>
            {/* Aggregate card */}
            <div
              style={{
                background: '#fff', borderRadius: 16, border: '1px solid #e5e7eb',
                padding: '24px 28px', marginBottom: 24,
              }}
            >
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 24, alignItems: 'flex-start' }}>
                {/* Big rating */}
                <div style={{ textAlign: 'center', minWidth: 100 }}>
                  <div style={{ fontSize: 52, fontWeight: 800, color: '#104d98', lineHeight: 1 }}>
                    {avgRating?.toFixed(1)}
                  </div>
                  <div style={{ marginTop: 6 }}>
                    <StarRow value={avgRating} size={22} />
                  </div>
                  <div style={{ fontSize: 13, color: '#9ca3af', marginTop: 4 }}>
                    {totalCount} {totalCount === 1 ? 'recensione' : 'recensioni'}
                  </div>
                </div>

                {/* Distribution bars */}
                <div style={{ flex: 1, minWidth: 200 }}>
                  {dist?.map(({ star, count }) => (
                    <DistributionBar key={star} label={star} count={count} total={totalCount} />
                  ))}
                </div>
              </div>
            </div>

            {/* Review list */}
            {reviews?.map((review) => (
              <ReviewCard
                key={review?.id}
                review={review}
                onResponseSaved={handleResponseSaved}
                onLightbox={setLightbox}
              />
            ))}
          </>
        )}
      </div>
      <Footer />
      {toast && <Toast message={toast} onClose={() => setToast(null)} />}
      {lightbox && (
        <Lightbox
          lightbox={lightbox}
          onClose={() => setLightbox(null)}
          onPrev={() => setLightbox(prev => ({ ...prev, currentIndex: prev?.currentIndex - 1 }))}
          onNext={() => setLightbox(prev => ({ ...prev, currentIndex: prev?.currentIndex + 1 }))}
        />
      )}
    </div>
  );
};

export default ProfessionalRecensioni;
